import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

type UserMode = 'consultant' | 'manager' | 'client' | 'business';
export type BusinessType = 'medical' | 'hospitality' | 'beauty' | 'fitness' | 'shop' | 'other';

interface NavigationContextType {
  // Current mode based on URL or admin selection
  currentMode: UserMode;

  // Current business type based on URL or admin selection (only relevant for business mode)
  currentBusinessType: BusinessType;

  // User's actual role from database
  userRole: string | null;

  // Available modes for the current user
  availableModes: UserMode[];

  // Navigation helpers
  navigateTo: (path: string, options?: { replace?: boolean }) => void;
  navigateToDashboard: () => void;
  navigateToProjects: () => void;
  navigateToCreateProject: () => void;
  navigateToProject: (projectId: string, section?: string) => void;
  navigateToMessages: () => void;
  navigateToProfile: () => void;
  navigateToContacts: () => void;
  navigateToNotifications: () => void;
  navigateToNotes: () => void;
  navigateToCalendar: () => void;
  navigateToResearch: () => void;
  navigateToClients: () => void;
  navigateToCompany: () => void;
  navigateToBilling: () => void;
  navigateToUsage: () => void;
  navigateToBusinessDashboard: () => void;
  navigateToBusinessChat: () => void;
  navigateToBusinessContacts: () => void;
  navigateToBusinessAppointments: () => void;
  navigateToBusinessSettings: () => void;

  // Mode switching (for admin users)
  switchMode: (mode: UserMode) => void;

  // Business type switching (for admin users)
  switchBusinessType: (type: BusinessType) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  // User's actual role from database
  const userRole = profile?.role || null;

  // For admin users, store their selected mode in localStorage
  const [adminSelectedMode, setAdminSelectedMode] = useState<UserMode | null>(() => {
    if (userRole === 'admin') {
      const stored = localStorage.getItem(`adminMode_${user?.id}`);
      return stored as UserMode || 'consultant';
    }
    return null;
  });

  // State to store the actual business type slug assigned to the user (for non-admins)
  const [userBusinessSlug, setUserBusinessSlug] = useState<string | null>(null);

  // Fetch user's business type slug
  useEffect(() => {
    const fetchUserBusinessType = async () => {
      if (!user || userRole === 'admin') return;

      try {
        const { data: whatsappData, error: whatsappError } = await supabase
          .from('whatsapp_accounts')
          .select('business_type_id')
          .eq('user_id', user.id)
          .single();

        if (whatsappError || !whatsappData?.business_type_id) {
          // Check if we should log this or just fail silently
          return;
        }

        const { data: typeData, error: typeError } = await supabase
          .from('business_types')
          .select('slug')
          .eq('id', whatsappData.business_type_id)
          .single();

        if (typeData?.slug) {
          setUserBusinessSlug(typeData.slug);
        }
      } catch (error) {
        console.error('Error fetching business type:', error);
      }
    };

    fetchUserBusinessType();
  }, [user, userRole]);

  // For admin users, store their selected business type in localStorage
  const [adminSelectedBusinessType, setAdminSelectedBusinessType] = useState<BusinessType | null>(() => {
    if (userRole === 'admin') {
      const stored = localStorage.getItem(`adminBusinessType_${user?.id}`);
      return stored as BusinessType || 'other';
    }
    return null;
  });

  // Determine current mode
  const getCurrentMode = (): UserMode => {
    // For admin users, use their selected mode if set, otherwise derive from URL
    if (userRole === 'admin') {
      if (adminSelectedMode) {
        return adminSelectedMode;
      }
    }

    // For all users, derive mode from URL path
    if (location.pathname.startsWith('/clientportal')) return 'client';
    if (location.pathname.startsWith('/manager')) return 'manager';
    if (location.pathname.startsWith('/business')) return 'business';
    return 'consultant';
  };

  const currentMode = getCurrentMode();

  // Determine current business type
  // Determine current business type
  const getCurrentBusinessType = (): BusinessType => {
    // 1. Priority: Try to extract from URL
    // This ensures that if the user navigates (back/forward) or types a URL, it is respected.
    if (location.pathname.startsWith('/business/')) {
      const parts = location.pathname.split('/');
      // /business/:type/...
      if (parts.length >= 3) {
        const type = parts[2] as BusinessType;
        if (['medical', 'hospitality', 'beauty', 'fitness', 'shop', 'other'].includes(type)) {
          return type;
        }
      }
    }

    // 2. Secondary: For admin users, use their selected type if in business mode
    if (userRole === 'admin' && currentMode === 'business') {
      if (adminSelectedBusinessType) {
        return adminSelectedBusinessType;
      }
    }

    // 3. Default fallback
    return 'other';
  };

  const currentBusinessType = getCurrentBusinessType();

  // Enforce access control for business users
  useEffect(() => {
    if (currentMode === 'business' && userRole === 'business' && userBusinessSlug) {
      if (currentBusinessType !== userBusinessSlug) {
        // Redirect to their assigned buisness type
        navigate(`/business/${userBusinessSlug}/dashboard`, { replace: true });
        toast({
          title: "Access Restricted",
          description: `You have been redirected to your assigned business portal.`,
          variant: "destructive",
        });
      }
    }
  }, [currentMode, currentBusinessType, userRole, userBusinessSlug, navigate]);

  // Available modes for the current user
  const getAvailableModes = (): UserMode[] => {
    if (!userRole) return [];
    if (userRole === 'admin') return ['consultant', 'client', 'manager', 'business'];
    if (userRole === 'business') return ['business'];
    if (userRole === 'consultant') return ['consultant'];
    if (userRole === 'manager') return ['manager'];
    if (userRole === 'client') return ['client'];
    return ['consultant']; // Default fallback
  };

  const availableModes = getAvailableModes();

  // Update admin mode when URL changes (for consistency)
  useEffect(() => {
    if (userRole === 'admin') {
      const urlMode = getCurrentMode();
      if (urlMode !== adminSelectedMode) {
        setAdminSelectedMode(urlMode);
        localStorage.setItem(`adminMode_${user?.id}`, urlMode);
      }

      // Also update business type if we are in business mode
      if (urlMode === 'business') {
        const urlBusinessType = getCurrentBusinessType();
        if (urlBusinessType !== adminSelectedBusinessType) {
          setAdminSelectedBusinessType(urlBusinessType);
          localStorage.setItem(`adminBusinessType_${user?.id}`, urlBusinessType);
        }
      }
    }
  }, [location.pathname, userRole, user?.id, adminSelectedMode, adminSelectedBusinessType]);

  // Navigation helpers
  // Navigation helpers
  const getBasePath = () => {
    if (currentMode === 'client') return '/clientportal';
    if (currentMode === 'business') return `/business/${currentBusinessType}`;
    return `/${currentMode}`;
  };

  const navigateTo = (path: string, options?: { replace?: boolean }) => {
    navigate(path, options);
  };

  const navigateToDashboard = () => {
    navigate(`${getBasePath()}/dashboard`);
  };

  const navigateToProjects = () => {
    navigate(`${getBasePath()}/projects`);
  };

  const navigateToCreateProject = () => {
    navigate(`${getBasePath()}/create-project`);
  };

  const navigateToProject = (projectId: string, section: string = 'overview') => {
    navigate(`${getBasePath()}/project/${projectId}/${section}`);
  };

  const navigateToMessages = () => {
    navigate(`${getBasePath()}/messages`);
  };

  const navigateToProfile = () => {
    navigate(`${getBasePath()}/profile/general`);
  };

  const navigateToContacts = () => {
    navigate(`${getBasePath()}/contacts`);
  };

  const navigateToNotifications = () => {
    navigate(`${getBasePath()}/notifications`);
  };

  const navigateToNotes = () => {
    navigate(`${getBasePath()}/notes`);
  };

  const navigateToCalendar = () => {
    navigate(`${getBasePath()}/calendar`);
  };

  const navigateToResearch = () => {
    navigate(`${getBasePath()}/research`);
  };

  const navigateToClients = () => {
    navigate(`${getBasePath()}/clients`);
  };

  const navigateToCompany = () => {
    navigate(`${getBasePath()}/company`);
  };

  const navigateToBilling = () => {
    navigate(`${getBasePath()}/billing`);
  };

  const navigateToUsage = () => {
    navigate(`${getBasePath()}/usage`);
  };

  const navigateToBusinessDashboard = () => {
    navigate(`/business/${currentBusinessType}/dashboard`);
  };

  const navigateToBusinessChat = () => {
    navigate(`/business/${currentBusinessType}/chat`);
  };

  const navigateToBusinessContacts = () => {
    navigate(`/business/${currentBusinessType}/contacts`);
  };

  const navigateToBusinessAppointments = () => {
    navigate(`/business/${currentBusinessType}/appointments`);
  };

  const navigateToBusinessSettings = () => {
    navigate(`/business/${currentBusinessType}/settings/chatbot`);
  };

  const switchMode = (mode: UserMode) => {
    if (userRole === 'admin' && availableModes.includes(mode)) {
      setAdminSelectedMode(mode);
      localStorage.setItem(`adminMode_${user?.id}`, mode);
      if (mode === 'client') {
        navigate('/clientportal/dashboard');
      } else if (mode === 'business') {
        navigate('/business/other/dashboard');
      } else {
        navigate(`/${mode}/dashboard`);
      }
    }
  };

  const switchBusinessType = (type: BusinessType) => {
    if (userRole === 'admin') {
      setAdminSelectedBusinessType(type);
      localStorage.setItem(`adminBusinessType_${user?.id}`, type);
      // Navigate to the dashboard of the new type
      navigate(`/business/${type}/dashboard`);
    }
  };

  const value: NavigationContextType = {
    currentMode,
    userRole,
    availableModes,
    navigateTo,
    navigateToDashboard,
    navigateToProjects,
    navigateToCreateProject,
    navigateToProject,
    navigateToMessages,
    navigateToProfile,
    navigateToContacts,
    navigateToNotifications,
    navigateToNotes,
    navigateToCalendar,
    navigateToResearch,
    navigateToClients,
    navigateToCompany,
    navigateToBilling,
    navigateToUsage,
    navigateToBusinessDashboard,
    navigateToBusinessChat,
    navigateToBusinessContacts,
    navigateToBusinessAppointments,
    navigateToBusinessSettings,
    switchMode,
    currentBusinessType,
    switchBusinessType,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
